import { 
  FolderInput, 
  CheckSquare, 
  Package, 
  DollarSign, 
  Calendar, 
  Truck 
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

type WorkflowStatus = 
  | "Completed" 
  | "Validated" 
  | "In Progress" 
  | "Pending" 
  | "Not Started" 
  | "Failed";

interface WorkflowStep {
  id: number;
  title: string;
  subtitle: string;
  status: WorkflowStatus;
  icon: React.ReactNode;
}

interface WorkflowVisualizationProps {
  currentStepId: number;
  progress: number;
}

export const WorkflowVisualization = ({ 
  currentStepId, 
  progress 
}: WorkflowVisualizationProps) => {
  const workflowSteps: WorkflowStep[] = [
    {
      id: 1,
      title: "Planning",
      subtitle: "Sanity & Inventory Check",
      status: "Completed",
      icon: <FolderInput className="h-5 w-5 text-success" />
    },
    {
      id: 2,
      title: "Appointment Scheduling",
      subtitle: "Date & Time Setup",
      status: "Validated",
      icon: <Calendar className="h-5 w-5 text-success" />
    },
    {
      id: 3,
      title: "Sales Order Processing",
      subtitle: "Order Management",
      status: "In Progress",
      icon: <CheckSquare className="h-5 w-5 text-info" />
    },
    {
      id: 4,
      title: "Courier Operations",
      subtitle: "Pickup & Transport",
      status: "Pending",
      icon: <Truck className="h-5 w-5 text-gray-400" />
    },
    {
      id: 5,
      title: "Invoicing",
      subtitle: "Bill Generation",
      status: "Not Started",
      icon: <DollarSign className="h-5 w-5 text-gray-400" />
    },
    {
      id: 6,
      title: "Warehouse Dispatch",
      subtitle: "Final Fulfillment",
      status: "Not Started",
      icon: <Package className="h-5 w-5 text-gray-400" />
    }
  ];

  const getStatusBadgeVariant = (status: WorkflowStatus): "success" | "info" | "warning" | "pending" | "destructive" => {
    switch (status) {
      case "Completed":
      case "Validated":
        return "success";
      case "In Progress":
        return "info";
      case "Pending":
        return "warning";
      case "Failed":
        return "destructive";
      default:
        return "pending";
    }
  };

  const getStepClassName = (step: WorkflowStep) => {
    if (step.id < currentStepId) {
      return "completed";
    } else if (step.id === currentStepId) {
      return "current";
    } else {
      return "upcoming";
    }
  };

  return (
    <div className="workflow-visualization relative">
      <div className="flex flex-nowrap space-x-6 pb-10 pt-4 overflow-x-auto">
        {workflowSteps.map((step) => (
          <div 
            key={step.id} 
            className={cn(
              "workflow-step relative flex flex-col items-center min-w-[120px]",
              getStepClassName(step)
            )}
          >
            <div className={cn(
              "rounded-full p-3 flex items-center justify-center h-14 w-14",
              step.status === "Completed" || step.status === "Validated" 
                ? "bg-success/10" 
                : step.status === "In Progress" 
                  ? "bg-info/10" 
                  : step.status === "Failed" 
                    ? "bg-destructive/10" 
                    : "bg-gray-200"
            )}>
              {step.icon}
            </div>
            <div className="mt-2 text-sm font-medium text-center text-gray-900">
              {step.title}
            </div>
            <div className="mt-1 text-xs text-center text-gray-500">
              {step.subtitle}
            </div>
            <Badge 
              variant={getStatusBadgeVariant(step.status)}
              className="mt-2"
            >
              {step.status}
            </Badge>
          </div>
        ))}
      </div>
      <Progress value={progress} className="mt-4 w-full" />
    </div>
  );
};
